import { Icon } from "leaflet";
import { useMemo } from "react";
import {
  MapContainer,
  Marker,
  Polyline,
  Popup,
  TileLayer,
} from "react-leaflet";
import { useSelector } from "react-redux";
import getLogsFromAlcoholData, {
  Log,
  LogWithUserId,
} from "src/lib/getLogsFromAlcoholData";
import useGetEventByType from "src/lib/hooks/useGetEventByType";
import { DrunkDrink, Event, Photo } from "src/lib/slices/alcoholSlice";
import { getDrinkImageUrl } from "./DrinkImage";
import { transformImgSrc } from "./Image";
import "./RouteMap.css";
import DrinkLogCard from "./drink-log/DrinkLogCard";
import EventLogCard from "./drink-log/EventLogCard";
import PhotoLogCard from "./drink-log/PhotoLogCard";
import { AlcDrawer } from "./ui/drawer";
import { Card, CardContent } from "./ui/card";

export function MapDrawer({
  logs,
  logToCenter,
  route,
  selectOwnLogs,
  selectOwnRoute,
  asChild,
  children,
  showPath,
}: {
  logs?: Log[];
  logToCenter?: Log;
  route?: { latitude: number; longitude: number; timestamp: number }[];
  selectOwnLogs?: boolean;
  selectOwnRoute?: boolean;
  children: React.ReactNode;
  asChild?: boolean;
  showPath?: boolean;
}) {
  const OwnLogs = getLogsFromAlcoholData(
    useSelector((state: any) => state.alcohol)
  );
  const OwnRoute = useSelector((state: any) => state.alcohol.route);

  return (
    <AlcDrawer
      trigger={children}
      asChild={asChild}
      expandContent
      contentPadding="0"
    >
      <div style={{ paddingTop: "1rem", height: "100%" }} data-vaul-no-drag>
        <RouteMap
          logs={selectOwnLogs ? OwnLogs : logs}
          route={selectOwnRoute ? OwnRoute : route}
          logToCenter={logToCenter}
          showPath={showPath}
        />
      </div>
    </AlcDrawer>
  );
}

function DrinkMarker({ drunkDrink }: { drunkDrink: DrunkDrink }) {
  const icon = new Icon({
    iconUrl: getDrinkImageUrl(drunkDrink.drink),
    iconSize: [40, 40],
  });

  return (
    <Marker
      position={[drunkDrink.latitude, drunkDrink.longitude]}
      key={drunkDrink.id}
      icon={icon}
    >
      <Popup>
        <DrinkLogCard
          drunkDrink={drunkDrink}
          allowDeletion={false}
          allowMapPreview={false}
        />
      </Popup>
    </Marker>
  );
}

function EventMarker({ event }: { event: Event }) {
  const user = useSelector((state: any) => state.user);

  const eventType = useGetEventByType(event.type);

  const icon = new Icon({
    iconUrl: eventType?.icon
      ? `/event-icons/${eventType?.icon}`
      : transformImgSrc(user?.photo) ?? "/event-icons/neutral.svg",
    iconSize: [40, 40],
  });

  return (
    <Marker
      position={[event.latitude, event.longitude]}
      key={event.id}
      icon={icon}
    >
      <Popup>
        <EventLogCard
          event={event}
          allowDeletion={false}
          allowMapPreview={false}
        />
      </Popup>
    </Marker>
  );
}

function PhotoMarker({ photo }: { photo: Photo }) {
  const icon = new Icon({
    iconUrl: transformImgSrc(photo.photoData),
    iconSize: [40, 40],
  });

  return (
    <Marker
      position={[photo.latitude, photo.longitude]}
      key={photo.id}
      icon={icon}
    >
      <Popup>
        <Card>
          <CardContent className="p-4">
            <PhotoLogCard
              photo={photo}
              allowDeletion={false}
              allowMapPreview={false}
            />
          </CardContent>
        </Card>
      </Popup>
    </Marker>
  );
}

export default function RouteMap({
  logs,
  logToCenter,
  route,
  showPath = true,
}: {
  logs?: Log[] | LogWithUserId[];
  logToCenter?: Log;
  route?: { latitude: number; longitude: number; timestamp: number }[];
  showPath?: boolean;
}) {
  const logsWithLocation = useMemo(
    () => logs?.filter((log) => log.data.latitude && log.data.longitude) ?? [],
    [logs]
  );

  if (!logsWithLocation.length && logToCenter) {
    logsWithLocation.push(logToCenter);
  }

  const locationData = useSelector((state: any) => state.location);

  let center: [number, number];

  if (logToCenter)
    center = [logToCenter.data.latitude, logToCenter.data.longitude];

  const path: [number, number][] = useMemo(() => {
    let points: { location: [number, number]; timestamp: number }[] =
      logsWithLocation.map((x) => ({
        location: [x.data.latitude, x.data.longitude],
        timestamp: x.data.date,
      }));
    if (!route) return points.map((x) => x.location);
    route.forEach((point) => {
      if (
        !points.find(
          (x) =>
            x.timestamp === point.timestamp &&
            x.location[0] === point.latitude &&
            x.location[1] === point.longitude
        )
      )
        points.push({
          location: [point.latitude, point.longitude],
          timestamp: point.timestamp,
        });
    });

    points = points.sort((a, b) => a.timestamp - b.timestamp);

    return points.map((x) => x.location);
  }, [logsWithLocation, route]);

  if (!center && path.length > 0) {
    center = path[path.length - 1];
  }

  if (!center) {
    center = [
      locationData?.location?.latitude,
      locationData?.location?.longitude,
    ];
  }

  if (!center || !center[0] || !center[1]) {
    return (
      <div style={{ textAlign: "center" }}>
        Nemáme zatím žádná data pro zobrazení mapy
      </div>
    );
  }

  return (
    <div data-vaul-no-drag style={{ height: "100%" }}>
      <MapContainer
        center={center}
        zoom={13}
        scrollWheelZoom={false}
        style={{ height: "100%" }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {logsWithLocation.map((log) => {
          if (log.type === "drink")
            return <DrinkMarker drunkDrink={log.data} key={log.data.id} />;

          if (log.type === "event")
            return <EventMarker event={log.data} key={log.data.id} />;

          if (log.type === "photo")
            return <PhotoMarker photo={log.data} key={log.data.id} />;

          return null;
        })}
        {path.length > 0 && showPath && <Polyline positions={path} />}
      </MapContainer>
    </div>
  );
}
